import cx from 'classnames'

import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from '@/components/ModalSheet'
import { Button, RawButton } from '@/components/Button'

import styles from './ConsultationModal.module.css'
import { format } from 'date-fns'
import { CompletionToggle } from '@/components/CompletionToggle'
import { type AvatarSubject, Avatar } from '@/components/Avatar'

export interface ConsultationTimeSlot {
  startTime: Date
  endTime: Date
  selected: boolean
  isBooked: boolean
  onSelect: () => unknown
}

export interface ConsultationDay {
  label: string
  slots: ConsultationTimeSlot[]
}

export interface ConsultationModalProps {
  showing: boolean
  onClose: () => unknown
  onSubmit: () => unknown
  requestedLength: number
  days: ConsultationDay[]
  client: AvatarSubject
  currentMember: AvatarSubject
}

export function ConsultationModal({
  showing,
  onClose,
  onSubmit,
  requestedLength,
  days,
  client,
  currentMember,
}: ConsultationModalProps) {
  return (
    <ModalSheet showing={showing} onRequestClose={onClose}>
      <ModalSheetHeader withCloseButton></ModalSheetHeader>
      <ModalSheetContent leftAligned>
        <ModalSheetContentBlock>
          <ModalSheetContentTitle>
            <span className={styles.title}>Schedule a consultation call</span>
          </ModalSheetContentTitle>

          <ModalSheetContentDescription>
            <span className={styles.description}>
              {`This job requires a ${requestedLength} minute consultation call. To confirm job
              pickup, select a date and time to continue.`}
            </span>
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>
        <div className={styles.scheduledDays}>
          {days.map((d) => (
            <div key={d.label}>
              <div className={styles.dayTitle}>{d.label}</div>
              {d.slots.map((t, i) => (
                <TimeSlotListItem
                  key={i}
                  index={i}
                  slot={t}
                  client={client}
                  currentMember={currentMember}
                />
              ))}
            </div>
          ))}
        </div>
      </ModalSheetContent>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button label="Close" variant="default" onPress={onClose} />
          <Button
            label="Confirm Pickup"
            type="submit"
            variant="primary"
            onPress={onSubmit}
            testId="confirm-pickup-button"
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

interface TimeSlotListItemProps {
  index: number
  slot: ConsultationTimeSlot
  client: AvatarSubject
  currentMember: AvatarSubject
}

function TimeSlotListItem({
  index,
  slot,
  client,
  currentMember,
}: TimeSlotListItemProps) {
  const { isBooked, selected } = slot

  return (
    <RawButton
      className={cx(
        styles.timeSlotItem,
        selected && styles.selected,
        isBooked && styles.disabled,
      )}
      testId={`time-slot-item-${slot.startTime.getDay()}-${index}`}
      isDisabled={isBooked}
      onPress={() => slot.onSelect()}
    >
      <div className={styles.completionToggle}>
        <CompletionToggle
          checkType="tick"
          size={20}
          completed={selected}
          isDisabled={isBooked}
          readOnly
        />
      </div>
      <div className={styles.timeSlotText}>
        {selected && (
          <div className={styles.jobName}>
            {isBooked ? 'Unavailable' : 'Selected'}
          </div>
        )}
        {format(slot.startTime, 'h:mm')} - {format(slot.endTime, 'h:mmaaa')}
      </div>
      {selected && (
        <>
          <Avatar subject={client} size={24} greyscale={isBooked} />
          <div className={styles.avatars}>
            <Avatar subject={currentMember} size={24} greyscale={isBooked} />
          </div>
        </>
      )}
    </RawButton>
  )
}
